













































































import {
    EventFlowItem,
    ExtraFunctionFormData,
} from '@/model/event-flow/EventFlow';
import { Component, Prop, PropSync, Vue } from 'vue-property-decorator';
import { throttle } from 'throttle-debounce';
import { timeFormatOptions, newDateWithAddedDays } from '@/lib/date-util';

import {
    calculatePos,
    calculateNumberOfMovedSteps,
    isStartMovementOutOfBound,
    isEndMovementOutOfBound,
    addOrSubtractStepsFromDate,
} from '@/lib/helper/event-flow-helper';
import { PropType } from 'vue';

@Component
export default class GridItem extends Vue {
    @Prop() readonly eventFlowItem!: EventFlowItem;
    @Prop() readonly isSheet!: boolean;
    @Prop() readonly startDateTime!: Date;
    @Prop() readonly endDateTime!: Date;
    @Prop() readonly lastPos!: number;
    @Prop() readonly step!: number;
    @Prop() readonly timeValuesLength!: number;
    @Prop() readonly index!: number;

    @PropSync('extraData', {
        type: Object as PropType<ExtraFunctionFormData>,
        default: () => ({
            showTime: false,
            showBrief: false,
            showLegend: false,
        }),
    })
    readonly extraFunctionFormData!: ExtraFunctionFormData;

    timeFormatOptions = timeFormatOptions;
    movementFirstOffsetY = 0;
    prevClientY = 0;
    throttle = 10;
    moveX?: number = undefined;
    moveY?: number = undefined;

    get rowStart(): number {
        return calculatePos(
            this.eventFlowItem.startedAt,
            this.startDateTime,
            this.lastPos,
            this.step,
        );
    }

    get rowEnd(): number {
        return calculatePos(
            this.eventFlowItem.endedAt,
            this.startDateTime,
            this.lastPos,
            this.step,
        );
    }

    get rowHeight(): number {
        const gridHeight =
            document.getElementById('event-flow-grid')?.clientHeight;

        return gridHeight ? gridHeight / this.timeValuesLength : 48;
    }

    click(item: EventFlowItem): void {
        this.$root.$emit('click', item);
    }

    updateItem(item: EventFlowItem): void {
        this.$root.$emit('updateItem', item);
    }

    changeMoveItem(eventFlowItem: EventFlowItem | null): void {
        this.$root.$emit('changeMoveItem', eventFlowItem);
    }

    itemChangedLocal(eventFlowItem: EventFlowItem): void {
        this.$root.$emit('itemChangedLocal', eventFlowItem);
    }

    onDragStart(event: DragEvent): void {
        if (event.dataTransfer) {
            event.dataTransfer.setDragImage(new Image(), 0, 0);
        }
    }

    onDragStartMovement(event: DragEvent, eventFlowItem: EventFlowItem): void {
        this.movementFirstOffsetY = event.clientY;
        this.moveX = event.offsetX;
        this.moveY = event.offsetY;
        this.prevClientY = event.clientY;
        this.changeMoveItem(eventFlowItem);
        this.onDragStart(event);
    }

    throttleModifyStart = throttle(this.throttle, false, this.modifyStart);

    modifyStart(event: DragEvent, eventFlowItem: EventFlowItem): void {
        if (
            (event.clientX === 0 && event.clientY === 0) ||
            isStartMovementOutOfBound(
                event.offsetY,
                eventFlowItem.startedAt,
                this.startDateTime,
            )
        ) {
            return;
        }

        const numberOfMovedTiles = calculateNumberOfMovedSteps(
            event.offsetY,
            this.rowHeight,
        );

        const newEventFlowItem = { ...eventFlowItem };

        newEventFlowItem.startedAt = addOrSubtractStepsFromDate(
            eventFlowItem.startedAt,
            numberOfMovedTiles,
            this.step,
        );

        if (newEventFlowItem.endedAt > newEventFlowItem.startedAt) {
            this.itemChangedLocal(newEventFlowItem);
        }
    }

    onDragEndMovement(item: EventFlowItem): void {
        this.moveX = undefined;
        this.moveY = undefined;
        this.updateItem(item);
        this.changeMoveItem(null);
    }

    isMoved(offsetX: number, offsetY: number): boolean {
        if (offsetX !== this.moveX || offsetY !== this.moveY) {
            this.moveX = offsetX;
            this.moveY = offsetY;
            return true;
        }

        return false;
    }

    throttleModifyEnd = throttle(this.throttle, false, this.modifyEnd);

    modifyEnd(event: DragEvent, eventFlowItem: EventFlowItem): void {
        if (
            (event.clientX === 0 && event.clientY === 0) ||
            isEndMovementOutOfBound(
                event.offsetY,
                eventFlowItem.endedAt,
                this.endDateTime,
            )
        ) {
            return;
        }

        const numberOfMovedTiles = calculateNumberOfMovedSteps(
            event.offsetY,
            this.rowHeight,
        );

        const newEventFlowItem = { ...eventFlowItem };

        newEventFlowItem.endedAt = addOrSubtractStepsFromDate(
            eventFlowItem.endedAt,
            numberOfMovedTiles,
            this.step,
        );

        if (newEventFlowItem.endedAt > newEventFlowItem.startedAt) {
            this.itemChangedLocal(newEventFlowItem);
        }
    }

    throttleMoveItem = throttle(this.throttle, false, this.moveItem);

    moveItem(event: DragEvent, eventFlowItem: EventFlowItem): void {
        if (!this.isMoved(event.offsetX, event.offsetY)) {
            return;
        }

        const calculatedMovement = event.clientY - this.movementFirstOffsetY;

        if (
            (event.clientX === 0 && event.clientY === 0) ||
            Math.abs(this.prevClientY - event.clientY) < this.rowHeight
        ) {
            return;
        }

        const numberOfMovedTiles = calculateNumberOfMovedSteps(
            calculatedMovement,
            this.rowHeight,
        );

        const newEventFlowItem = { ...eventFlowItem };

        newEventFlowItem.startedAt = addOrSubtractStepsFromDate(
            eventFlowItem.startedAt,
            numberOfMovedTiles,
            this.step,
        );

        newEventFlowItem.endedAt = addOrSubtractStepsFromDate(
            eventFlowItem.endedAt,
            numberOfMovedTiles,
            this.step,
        );

        this.itemChangedLocal(newEventFlowItem);
        this.movementFirstOffsetY = event.clientY;
        this.prevClientY = event.clientY;

        if (
            (newEventFlowItem.startedAt < this.startDateTime &&
                newEventFlowItem.endedAt > this.endDateTime) ||
            (newEventFlowItem.startedAt < this.startDateTime &&
                newEventFlowItem.endedAt > this.startDateTime) ||
            (newEventFlowItem.endedAt < this.endDateTime &&
                newEventFlowItem.startedAt < this.startDateTime)
        ) {
            this.movementFirstOffsetY = event.clientY;
        }
    }

    canMoveTop(eventFlowItem: EventFlowItem): boolean {
        return (
            eventFlowItem.startedAt >= this.startDateTime &&
            eventFlowItem.startedAt.getDate() === this.startDateTime.getDate()
        );
    }

    canMoveBottom(eventFlowItem: EventFlowItem): boolean {
        return (
            (eventFlowItem.endedAt <= this.endDateTime &&
                eventFlowItem.endedAt.getDate() ===
                    this.endDateTime.getDate()) ||
            (eventFlowItem.endedAt <= this.endDateTime &&
                eventFlowItem.endedAt.getDate() ===
                    newDateWithAddedDays(this.endDateTime, -1).getDate()) ||
            eventFlowItem.endedAt.getHours() === 0
        );
    }
}
